/**
 * Changelog des nouveautés visibles par les marques.
 *
 * RITUEL À CHAQUE MISE EN LIGNE
 * -----------------------------
 * 1. Ajouter une entrée AU DÉBUT du tableau `changelogEntries`.
 *    L'`id` doit être unique et stable (ex: '2026-04-19-pricing-intelligence').
 * 2. `publishedAt` = date du push en ISO (YYYY-MM-DD), c'est lui qui sert au TTL.
 * 3. `summary` = 1 phrase qui apparaît dans le bandeau (max ~80 caractères).
 * 4. `body` = description complète qui s'affiche dans le drawer au clic.
 *    Markdown léger supporté (gras `**`, listes `-`, sauts de ligne).
 * 5. Si la feature mérite une explication plus longue côté assistant FAQ,
 *    pense aussi à ajouter un topic dans `controllers/faq.js` (backend).
 *
 * COMPORTEMENT
 * ------------
 * - Une entrée s'affiche pendant `ttlDays` jours (7 par défaut) après `publishedAt`.
 * - Une entrée que la marque a marquée comme lue ne s'affiche plus jamais.
 * - L'état "lu" est stocké dans localStorage, par `id`.
 */

export interface ChangelogEntry {
  id: string
  publishedAt: string
  title: string
  summary: string
  body: string
  emoji?: string
  badge?: 'new' | 'improved' | 'fix'
  ttlDays?: number
}

export const DEFAULT_TTL_DAYS = 7

export const changelogEntries: ChangelogEntry[] = [
  {
    id: '2026-10-06-messaging-tips',
    publishedAt: '2026-10-06',
    title: 'Envoyer un tips depuis la conversation',
    summary: 'Ajoutez un bonus au créateur directement depuis la messagerie et la fiche BA.',
    emoji: '💸',
    badge: 'new',
    body: `Vous pouvez désormais **envoyer un tips** (bonus) à un créateur **sans quitter la conversation**, y compris **après la clôture de la campagne**.

**Où le trouver :**
- Dans l’onglet **Messagerie**, sur une conversation liée à une collab gagnée.
- Dans le **volet de discussion** d’une candidature et sur la **fiche profil créateur**.
- Partout, le même bouton « Envoyer un tips » ouvre directement la fenêtre d’envoi.

**Ce que ça change pour vous :**
- Plus besoin de repasser par d’autres écrans pour verser un **bonus ponctuel**.
- Le bouton n’apparaît que sur une **collab gagnée** avec votre marque.
- Vérification auto des **crédits** et redirection vers le **top‑up** si nécessaire.
- La transaction est tracée comme d’habitude (événement \`ba:tip\`).
`,
  },
  {
    id: '2026-07-12-videos-finales-par-defaut',
    publishedAt: '2026-07-12',
    title: 'Les vidéos finales s’affichent en premier',
    summary: 'Sur une candidature en step 2, les vidéos finales sont affichées par défaut et bien centrées.',
    emoji: '🎬',
    badge: 'improved',
    body: `Sur la page d'un créateur passé en **step 2** (contenu final), la vue s'ouvre désormais directement sur ses **vidéos finales** au lieu des vidéos initiales.

**Ce que ça change pour vous :**
- Plus besoin de cliquer sur « vidéos finales » à chaque fois : c'est la vue par défaut dès qu'un contenu final est disponible.
- Vous pouvez toujours revenir aux **vidéos initiales** d'un clic.
- La grille des vidéos est maintenant **centrée** (fini le décalage vers la gauche).`,
  },
  {
    id: '2026-07-08-voir-mon-brief',
    publishedAt: '2026-07-08',
    title: 'Revoyez votre brief à tout moment',
    summary: 'Un bouton « Voir mon brief » sur vos campagnes en cours pour relire consignes et script.',
    emoji: '📄',
    badge: 'new',
    body: `Jusqu'ici, une fois votre campagne lancée, vous n'aviez plus de moyen simple de relire le brief que vous aviez configuré (consignes, scénario, script, musique…).

**Ce que ça change pour vous :**
- Un bouton **« Voir mon brief »** apparaît désormais en haut de chaque campagne en cours (et sur vos deals privés).
- Il ouvre un **aperçu complet en lecture seule** : description, prérequis, scénario, script (PDF), musique, liens.
- Le bouton **« Afficher la vue in-app »** vous montre le brief exactement comme le voient les créateurs.

Pratique pour vérifier une consigne ou partager le contexte en interne, sans risque de modifier une campagne déjà active.`,
  },
  {
    id: '2026-07-07-telephone-createur-onspot',
    publishedAt: '2026-07-07',
    title: 'Appelez vos créateurs onspot en 1 clic',
    summary: 'Le numéro du créateur s’affiche sur la carte rendez-vous des campagnes onspot.',
    emoji: '📞',
    badge: 'new',
    body: `Sur vos campagnes onspot, à l'étape 2 (« Rendez-vous créateurs »), le numéro de téléphone du créateur s'affiche désormais directement sur sa carte de rendez-vous.

**Ce que ça change pour vous :**
- Le numéro est **cliquable** : un tap et l'appel se lance (idéal depuis mobile).
- Un **bouton copier** à côté pour récupérer le numéro en un clic.
- Plus besoin d'aller chercher l'info ailleurs pour organiser le rendez-vous en boutique.

Le numéro n'apparaît que pour les créateurs passés en étape 2, comme sur les campagnes « with product ».`,
  },
  {
    id: '2026-07-01-modal-litige-publication',
    publishedAt: '2026-07-01',
    title: 'Signalement de publication plus lisible',
    summary: 'La fenêtre pour signaler un problème de publication est de nouveau bien affichée.',
    emoji: '🛠️',
    badge: 'fix',
    body: `Sur vos campagnes influence, la fenêtre qui s'ouvre via l'icône « ! » (pour signaler un problème dans une publication) s'affichait de façon étriquée, avec un texte compressé.

**Ce que ça change pour vous :**
- La fenêtre occupe correctement toute sa largeur : titre, explication et menu de motif enfin lisibles.
- Sélection du motif et bouton d'envoi mis en page proprement.`,
  },
  {
    id: '2026-06-30-selecteur-marques',
    publishedAt: '2026-06-30',
    title: 'Retrouvez vos marques en un clin d’œil',
    summary: 'Page de sélection des marques repensée : recherche + tri par activité récente.',
    emoji: '🏷️',
    badge: 'improved',
    body: `Fini le scroll interminable pour retrouver la bonne marque (surtout avec beaucoup de marques).

**Ce que ça change pour vous :**
- Une **barre de recherche** pour filtrer vos marques par nom instantanément.
- Un **tri** au choix : activité récente (par défaut), A→Z, Z→A, ou messages non lus.
- Les marques avec une **campagne/deal en cours** remontent automatiquement en haut ; celles inactives depuis longtemps ne sont plus mises en avant.
- Affichage plus clair avec logo, badge « en cours » et messages non lus.

Cliquez simplement sur une marque pour accéder à ses campagnes.`,
  },
  {
    id: '2026-06-30-deal-prive-validation-unique',
    publishedAt: '2026-06-30',
    title: 'Validation de deal privé fiabilisée',
    summary: 'Un deal privé ne peut plus être validé (ni facturé) en double par erreur.',
    emoji: '🔒',
    badge: 'fix',
    body: `Dans de rares cas, valider un deal privé un peu trop vite pouvait l'enregistrer plusieurs fois d'affilée.

**Ce que ça change pour vous :**
- Un clic = une seule validation, **un seul deal envoyé au créateur**.
- Plus de risque de double ou triple débit lors de la validation.

Si vous aviez constaté ce comportement, contactez-nous : on régularise tout de suite.`,
  },
  {
    id: '2026-06-30-messagerie-sans-limite',
    publishedAt: '2026-06-30',
    title: 'Échangez sans blocage avec vos créateurs',
    summary: 'Plus de message « limite » quand la campagne est terminée : la conversation reste ouverte.',
    emoji: '💬',
    badge: 'fix',
    body: `Vous pouviez tomber sur un message vous proposant de payer pour continuer à écrire à un créateur, alors que votre compte le permettait déjà.

**Ce que ça change pour vous :**
- Vous gardez la main pour échanger avec un créateur que vous avez déjà sollicité, **que la campagne soit en cours ou terminée**.
- Plus de fausse fenêtre de paiement quand le canal de discussion existe déjà.

Le canal reste celui de vos échanges habituels, rien ne change dans votre messagerie.`,
  },
  {
    id: '2026-06-29-messagerie-non-lus',
    publishedAt: '2026-06-29',
    title: 'Une messagerie plus claire',
    summary: 'Filtre « Non lus », bouton « Tout marquer comme lu » et compteurs fiabilisés.',
    emoji: '📨',
    badge: 'improved',
    body: `On a fiabilisé et clarifié votre messagerie.

**Ce que ça change pour vous :**
- Un nouveau filtre **« Non lus »** pour retrouver d'un coup d'œil les conversations en attente de réponse.
- Un bouton **« Tout marquer comme lu »** pour remettre vos compteurs à zéro instantanément.
- Le compteur de notifications (badge de marque) est désormais **exact** : fini les notifications fantômes qui n'apparaissaient nulle part dans vos discussions.`,
  },
  {
    id: '2026-06-29-notifs-temps-reel',
    publishedAt: '2026-06-29',
    title: 'Messages et vidéos en temps réel',
    summary: 'Notifications cliquables instantanées pour vos messages et vos nouvelles vidéos.',
    emoji: '🔔',
    badge: 'improved',
    body: `Plus besoin de rafraîchir la page pour suivre votre activité.

**Ce que ça change pour vous :**
- Le compteur de messages non lus se met à jour **instantanément**.
- Une **notification élégante** apparaît quand un créateur vous écrit pendant que vous naviguez : cliquez dessus pour ouvrir directement la conversation.
- Une notification apparaît aussi dès qu'une **nouvelle vidéo** est validée dans l'un de vos castings : un clic vous emmène directement sur le contenu.
- La connexion temps réel est plus robuste et se rétablit toute seule après une coupure réseau ou une mise en veille.

Et si vous gérez **plusieurs marques**, chaque marque garde son propre compteur dans le sélecteur de marque.`,
  },
  {
    id: '2026-06-28-suivi-invitations',
    publishedAt: '2026-06-28',
    title: 'Suivez vos invitations dans la messagerie',
    summary: 'Filtre « Invitations », funnel de conversion et badges dans vos discussions.',
    emoji: '📊',
    badge: 'improved',
    body: `Vos invitations de créateurs sont maintenant suivies de bout en bout, directement dans l'onglet **Discussions**.

**Ce que ça change pour vous :**
- Un nouveau filtre **« Invitations »** regroupe toutes les conversations issues d'une invitation, même avec des créateurs que vous n'aviez jamais contactés.
- Un badge **« Invité(e) »** identifie ces créateurs dans la liste.
- Dans chaque conversation, une carte affiche la campagne concernée et l'avancement du créateur : **Invité → Vu → Inscrit → A postulé → Sélectionné**.
- Un bandeau récapitule votre **taux de conversion** global sur l'ensemble des invitations.

Les créateurs invités peuvent désormais **vous répondre directement** sans que vous ayez à les ajouter en favoris. Et pour protéger l'écosystème, les messages d'invitation sont analysés automatiquement : tout message tentant de sortir un créateur de Vogz est bloqué avant l'envoi (aucun crédit consommé).`,
  },
  {
    id: '2026-06-26-invitations-campagne',
    publishedAt: '2026-06-26',
    title: 'Invitez des créateurs à vos campagnes',
    summary: 'Sélectionnez des créateurs et invitez-les en un message groupé.',
    emoji: '📨',
    badge: 'new',
    body: `Vous manquez de candidats sur une campagne ? Vous pouvez désormais inviter directement des créateurs à y participer, par message groupé.

**Ce que ça change pour vous :**
- Depuis la **galerie créateurs**, sélectionnez plusieurs profils (bouton en haut à droite de chaque carte), puis cliquez sur « Inviter à une campagne ».
- Depuis la **fiche d'un créateur**, un bouton « Inviter à une campagne » est aussi disponible.
- Choisissez la campagne, personnalisez le message avec des variables (**[prenom]**, **[marque]**, **[campagne]**) et envoyez.
- Chaque créateur invité reçoit une notification **et** un e-mail avec un lien direct vers la campagne.

Les invitations utilisent des **crédits message** (1 crédit = 1 créateur invité), rechargeables par pack directement depuis la fenêtre d'invitation. Un même créateur ne peut pas être sollicité deux fois pour la même campagne.`,
  },
  {
    id: '2026-06-20-lien-public-campagne',
    publishedAt: '2026-06-20',
    title: 'Partagez le lien public de vos campagnes',
    summary: 'Copiez en un clic le lien public d\u2019une campagne pour la diffuser.',
    emoji: '🔗',
    badge: 'new',
    body: `Chaque campagne active dispose maintenant d\u2019un lien public partageable : une page où les créateurs peuvent découvrir le brief, voir le ciblage et postuler en créant un compte Vogz.

**Ce que ça change pour vous :**
- Depuis le menu d\u2019une campagne (vue cartes ou vue tableau), cliquez sur « Copier le lien public ».
- Diffusez ce lien sur vos réseaux, à votre communauté ou par e-mail pour booster le recrutement.

Le lien n\u2019apparaît que sur les campagnes ouvertes (non privées).`,
  },
  {
    id: '2026-06-20-hub-marque-unifie',
    publishedAt: '2026-06-20',
    title: 'Gestion de marque réunie au même endroit',
    summary: 'Identité, produits, invités et page publique dans un seul écran.',
    emoji: '🎯',
    badge: 'improved',
    body: `Fini de naviguer entre plusieurs menus pour gérer votre marque : tout est désormais regroupé dans un hub à onglets accessible directement depuis la barre latérale.

**Ce que ça change pour vous :**
- Une nouvelle entrée « Ma marque » dans le menu de gauche, toujours visible.
- Quatre onglets clairs : Identité & infos, Produits & adresses, Accès & invités, Programme public.
- Le nom de la marque est maintenant modifiable directement.
- Les comptes multi-marques peuvent changer de marque ou en ajouter une en un clic.

Les anciens liens d'édition redirigent automatiquement vers ce nouvel espace.`,
  },
  {
    id: '2026-06-19-programme-public-marque',
    publishedAt: '2026-06-19',
    title: 'Lancez votre programme créateurs public',
    summary: 'Une page publique brandée qui affiche vos campagnes en cours.',
    emoji: '🚀',
    badge: 'new',
    body: `Vous pouvez désormais activer une page publique brandée à votre marque, qui affiche en temps réel toutes vos campagnes en cours, comme votre propre feed de recrutement créateurs.

**Ce que ça change pour vous :**
- Activez le "Programme public" depuis la fiche de votre marque pour obtenir une URL dédiée (creator.vogz.io/m/votre-marque).
- Partagez ce lien à votre communauté, vos clients ou sur vos réseaux : tout le monde peut consulter vos campagnes, lire les briefs et postuler.
- Personnalisez le nom de l'URL et une accroche. La page se met à jour automatiquement quand vous lancez ou clôturez une campagne.

En un clic, vous lancez et diffusez votre propre programme créateurs.`,
  },
  {
    id: '2026-06-17-casting-profile-chat',
    publishedAt: '2026-06-17',
    title: 'Messages visibles dans les castings',
    summary: 'Les échanges créateurs restent accessibles depuis leur profil.',
    emoji: '💬',
    badge: 'improved',
    body: `La messagerie intégrée aux castings et aux profils créateurs a été renforcée.

**Ce que ça change pour vous :**
- Depuis une candidature active, vous pouvez échanger avec le créateur sans quitter le casting.
- L'historique reste visible sur la page du créateur même si son profil n'est pas en favori.
- Les anciennes protections anti-spam restent en place pour les discussions archivées ou perdues.`,
  },
  {
    id: '2026-06-15-base-creators-addon',
    publishedAt: '2026-06-15',
    title: 'Add-on Base Créateurs plus clair',
    summary: 'L’accès à la Base Créateurs affiche désormais son prix dédié.',
    emoji: '💡',
    badge: 'improved',
    body: `Le parcours d’accès à la Base Créateurs a été clarifié pour les marques déjà abonnées.

**Ce que ça change pour vous :**
- Si votre abonnement Premium est actif, l’écran affiche uniquement le prix de l’add-on Base Créateurs.
- Le paiement explique que votre abonnement principal reste inchangé.
- Les offres spécifiques négociées avec votre équipe Vogz restent prises en compte.`,
  },
  {
    id: '2026-06-10-step2-final-vogz-access',
    publishedAt: '2026-06-10',
    title: 'Accès aux VOGZ finales plus souple',
    summary: 'Les contenus finaux restent visibles même sans lien de suivi colis.',
    emoji: '🎬',
    badge: 'fix',
    body: `Le parcours des castings en deux étapes a été ajusté pour les cas où le produit n'est pas expédié.

**Ce que ça change pour vous :**
- Si un créateur envoie déjà son contenu final en step 2, l'onglet **VOGZ finales** devient accessible.
- Le lien de suivi colis n'est plus bloquant lorsque la marque gère l'achat produit autrement.
- Les contenus finaux restent consultables sans repasser par la modération step 1.`,
  },
  {
    id: '2026-06-10-creator-gallery-status-filter',
    publishedAt: '2026-06-10',
    title: 'Recherche créateurs plus fiable',
    summary: 'La galerie et l’Ad Library masquent les profils créateurs indisponibles.',
    emoji: '🔎',
    badge: 'fix',
    body: `La visibilité des profils créateurs a été fiabilisée dans le portail marque.

**Ce que ça change pour vous :**
- La galerie créateurs affiche uniquement les profils disponibles : **signed-up** ou **active**.
- Les profils supprimés ou incomplets ne sont plus proposés dans la recherche.
- Dans l'Ad Library, l'accès à une page créateur est désactivé si le profil n'est plus disponible.`,
  },
  {
    id: '2026-06-08-creator-offer-checkout',
    publishedAt: '2026-06-08',
    title: 'Commande d’offre créateur plus fluide',
    summary: 'Les liens d’offres créateurs affichent un parcours de paiement plus clair.',
    emoji: '🛒',
    badge: 'improved',
    body: `Le parcours de commande depuis une offre créateur partagée a été simplifié.

**Ce que ça change pour vous :**
- Si vous êtes déjà connecté, votre session est restaurée avant d'afficher l'offre.
- Le paiement parle désormais de **commande de collaboration**, sans vocabulaire de crédits.
- Après paiement Stripe, vous revenez directement sur l'offre pour finaliser la commande.`,
  },
  {
    id: '2026-06-01-direct-creator-offer-links',
    publishedAt: '2026-06-01',
    title: 'Liens d’offres créateurs plus directs',
    summary: 'Les liens d’offres créateurs ouvrent directement le bon parcours de commande.',
    emoji: '🤝',
    badge: 'improved',
    body: `Les liens partagés par les créateurs vers leurs offres Vogz sont désormais plus fluides pour vos clients externes.

**Ce que ça change pour vous :**
- Un lien d'offre créateur ouvre directement le parcours marque, sans écran de choix inutile.
- Après inscription ou connexion, vous revenez automatiquement sur l'offre à commander.
- Le paiement/top-up de la collaboration reste géré par Vogz, même sans abonnement préalable.`,
  },
  {
    id: '2026-04-19-search-ranking-by-reviews',
    publishedAt: '2026-04-19',
    title: 'Recherche créateurs boostée par les avis',
    summary: 'Les créateurs les mieux notés et les plus expérimentés remontent en tête de vos recherches.',
    emoji: '⭐',
    badge: 'improved',
    body: `Vos recherches de créateurs prennent désormais en compte **leurs avis et leur historique** pour faire remonter les profils les plus fiables.

**Ce qui change pour vous :**
- Les créateurs avec une **bonne note moyenne ET un nombre significatif d'avis** apparaissent en priorité.
- Un score lissé évite qu'un profil noté 5/5 sur un seul avis passe devant un profil noté 4.7/5 sur 50 avis.
- Les sous-critères (communication, qualité, ponctualité) sont remontés et pourront bientôt servir de filtres.

Vos retours après chaque casting servent directement à toute la communauté marques pour mieux choisir les bons profils.`,
  },
  {
    id: '2026-04-19-pricing-intelligence',
    publishedAt: '2026-04-19',
    title: 'Vogz Pricing Intelligence',
    summary: 'Un simulateur live qui vous indique le bon budget pour remplir vos castings.',
    emoji: '💸',
    badge: 'new',
    body: `Lors de la création d'un casting, un nouveau module **Vogz Pricing Intelligence** vous accompagne pour fixer la juste rémunération.

**Ce qu'il vous apporte :**
- Une **reco intelligente** : le prix qui remplit votre casting à 100 %, jamais sous la médiane marché.
- Un **% de complétion attendu** par paliers (Min / Reco) : vous voyez instantanément l'impact de chaque budget.
- Une **pulsation marché live** : nombre de castings actifs sur la même audience, médiane et écart vs marché.
- Des **suggestions d'audience** : élargir le genre ou la tranche d'âge augmente votre vivier réactif.

**Quand le vivier est plus ciblé** (audience pointue), le module bascule sur "candidatures qualifiées attendues" et vous suggère un élargissement plutôt que de pousser le prix dans le vide.

Tout est recalculé en live à chaque modification du brief.`,
  },
  {
    id: '2026-04-19-do-dont-ugc',
    publishedAt: '2026-04-19',
    title: 'Do & Don\'t pour vos briefs UGC',
    summary: 'Cadrez les attentes de vos créateurs en quelques bullet points.',
    emoji: '✅',
    badge: 'new',
    body: `Sur les briefs UGC, deux nouvelles sections **Do** et **Don't** sont disponibles dans la création/édition de casting.

**À quoi ça sert :**
- **Do** : les éléments incontournables (ton, mention de la marque, plan produit, etc.).
- **Don't** : ce qu'il ne faut surtout pas faire (musique commerciale, concurrents, certains visuels…).

Les créateurs voient ces consignes dès l'ouverture du brief, ce qui réduit drastiquement les contenus hors-sujet et les allers-retours en modération.

Astuce : restez concis (3 à 5 bullets max chaque). Les listes courtes sont mieux lues.`,
  },
]
